/**
 * AppLayout - Pure UI layout component for authenticated pages.
 * 
 * This component handles ONLY the visual layout:
 * - Sidebar navigation
 * - Header with logo, company name, notifications, user info
 * - Main content area
 * 
 * Authentication guards and realtime providers are handled by
 * AuthenticatedRoutesLayout which wraps all authenticated routes.
 */

import { ReactNode } from 'react';
import { SidebarProvider, SidebarTrigger, SidebarInset, useSidebar } from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useWorkplaceDetails } from '@/hooks/business/useWorkplaceDetails';
import { AppSidebar } from './AppSidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';

interface AppLayoutProps {
  children: ReactNode;
}

const AppLayout = ({ children }: AppLayoutProps) => {
  const isMobile = useIsMobile();

  return (
    <SidebarProvider defaultOpen={!isMobile}>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <AppLayoutContent>{children}</AppLayoutContent>
      </div>
    </SidebarProvider>
  );
};

/** Inner content that can access useSidebar() context */
function AppLayoutContent({ children }: { children: ReactNode }) {
  const { user, profile, currentCompany } = useAuth();
  const isMobile = useIsMobile();
  const { state } = useSidebar();

  const { data: workplaceDetails } = useWorkplaceDetails({ 
    enabled: !!(user && profile && currentCompany) 
  });

  const displayName = workplaceDetails?.business_name || currentCompany?.name;

  // Compute header left offset based on sidebar state (peer selectors don't reach nested children)
  const headerLeft = isMobile ? '0px' : state === 'collapsed' ? 'var(--sidebar-width-icon)' : 'var(--sidebar-width)';

  return (
    <SidebarInset className="flex-1 flex flex-col">
      {/* Header */}
      <header
        className="fixed top-0 right-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-[left] duration-200 ease-in-out"
        style={{ left: headerLeft }}
      >
        <div className="flex h-14 items-center gap-4 px-4 lg:px-6">
          <SidebarTrigger className="-ml-1 hover:bg-primary/10 hover:text-primary" />
          
          <div className="flex items-center gap-2 flex-1">
            {workplaceDetails?.logo_url ? (
              <img 
                src={workplaceDetails.logo_url} 
                alt="Company Logo"
                className="h-8 w-auto max-w-[200px] object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallbackText = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallbackText) {
                    fallbackText.style.display = 'block';
                  }
                }}
              />
            ) : (
              <h1 className="text-lg font-semibold md:text-xl bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Workforce
              </h1>
            )}
            
            {workplaceDetails?.logo_url && (
              <h1 className="text-lg font-semibold md:text-xl bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent" style={{ display: 'none' }}>
                Workforce
              </h1>
            )}
            
            {displayName && (
              <span className="text-sm text-muted-foreground hidden sm:inline">
                • {displayName}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <NotificationDropdown />
            {profile && (
              <span className="text-sm text-muted-foreground hidden md:inline">
                {profile.first_name} {profile.last_name}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Header Spacer */}
      <div className="h-14" />

      {/* Main Content */}
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        {children}
      </main>
    </SidebarInset>
  );
}

export default AppLayout;
